import { ReleaseNote } from "@/models/release-notes/ReleaseNote";
import {
  formatMonthYear,
  monthYearKey,
} from "@/components/release-notes/releaseNoteDate";

/**
 * Which release notes the changelog shows and whether any of them is unread.
 * Pure, so the dialog and the unread dot on the profile menu agree without
 * either reading the other's state.
 */

export type ReleaseNoteGroup = {
  key: string;
  label: string;
  notes: ReleaseNote[];
};

/** The notes the user has asked to see: technical ones only on request. */
export const visibleReleaseNotes = (
  notes: readonly ReleaseNote[],
  showTechnical: boolean
): ReleaseNote[] =>
  showTechnical ? [...notes] : notes.filter((note) => !note.isTechnical);

/**
 * Whether a visible note is newer than the last one the user saw.
 *
 * Compares positions in the full newest-first list rather than ids, so a
 * technical release at the top never nags a user who hides technical notes,
 * and showing them later does not mark an already-seen release unread. A
 * last-seen id the list no longer holds (deleted, or another environment)
 * counts as never seen.
 */
export const hasUnreadReleaseNote = (
  notes: readonly ReleaseNote[],
  visible: readonly ReleaseNote[],
  lastSeenId: string | null
): boolean => {
  if (visible.length === 0) return false;
  const seenIndex = lastSeenId
    ? notes.findIndex((note) => note.id === lastSeenId)
    : -1;
  if (seenIndex < 0) return true;
  return visible.some((note) => notes.indexOf(note) < seenIndex);
};

/** Groups notes (already newest-first) by month + year, preserving order. */
export const groupReleaseNotesByMonth = (
  notes: readonly ReleaseNote[]
): ReleaseNoteGroup[] => {
  const groups: ReleaseNoteGroup[] = [];
  const byKey = new Map<string, ReleaseNoteGroup>();
  for (const note of notes) {
    const key = monthYearKey(note.releaseDate);
    let group = byKey.get(key);
    if (!group) {
      group = { key, label: formatMonthYear(note.releaseDate), notes: [] };
      byKey.set(key, group);
      groups.push(group);
    }
    group.notes.push(note);
  }
  return groups;
};
